/**
 * Eventos disponíveis para o operador.
 *
 * Lê do banco local primeiro (portaria abre mesmo sem sinal) e atualiza do
 * servidor por cima. Escolher um evento leva direto para os setores — é lá que
 * a carga acontece, com progresso à vista.
 *
 * Cada cartão traz o que já está no aparelho: quantos ingressos baixados,
 * quantos setores e quantas entradas este caixa já liberou. É como o operador
 * reconhece, sem rede, se a base do evento desceu ou não.
 */

import React, {useCallback, useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Image,
  Pressable,
  RefreshControl,
  Text,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {ChevronRight, Lock, RefreshCw} from 'lucide-react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {baixarEventos, ErroApi} from '../api/cliente';
import {caixaAberto, fecharCaixa, vincularEvento, type Caixa} from '../caixa';
import {
  contagensDosEventos,
  listarEventos,
  removerEventosForaDaLista,
  salvarEventos,
  type ContagemEvento,
} from '../dados/repositorio';
import {carregarSessao} from '../sessao';
import type {Evento} from '../dominio/tipos';
import {comMilhar, quandoAcontece} from '../ui/datas';
import {txt, useIdioma} from '../ui/idioma';
import {estilos, fontes, useTema} from '../ui/tema';
import {Versao} from '../ui/Versao';
import type {ParamsPilha} from '../navegacao';

type Props = NativeStackScreenProps<ParamsPilha, 'Eventos'>;

const SEM_CONTAGEM: ContagemEvento = {ingressos: 0, setores: 0, nestaMaquina: 0};

/**
 * A arte do evento no cartão — o operador reconhece qual é antes de ler o
 * nome, que numa lista de "VALIDAÇÃO 2026" e "EVENTO 2026" ajuda.
 *
 * É enfeite, e some quando não dá: a imagem mora no site da ticketeira, e a
 * lista de eventos é justamente a tela que abre sem sinal. Falhou — sem rede,
 * endereço morto, conteúdo misto —, o cartão volta a ser o que sempre foi, em
 * vez de ficar com um buraco cinza do lado do nome.
 */
function BannerDoEvento({url}: {url?: string}) {
  const t = useTema();
  const [falhou, setFalhou] = useState(false);

  if (!url || falhou) {
    return null;
  }

  return (
    <Image
      // A página é https e o painel guarda endereço antigo em http, que o
      // navegador recusa como conteúdo misto. Subir o esquema custa uma
      // linha; se não houver https do outro lado, o onError resolve.
      source={{uri: url.replace(/^http:\/\//, 'https://')}}
      onError={() => setFalhou(true)}
      accessibilityLabel=""
      style={{width: 54, height: 54, borderRadius: 10, backgroundColor: t.superficieAlta}}
      resizeMode="cover"
    />
  );
}

export function TelaEventos({navigation}: Props) {
  const t = useTema();
  useIdioma();

  const [eventos, setEventos] = useState<Evento[]>([]);
  const [contagens, setContagens] = useState<Record<number, ContagemEvento>>({});
  const [caixa, setCaixa] = useState<Caixa | null>(null);
  const [atualizando, setAtualizando] = useState(false);
  /** Só alimenta o indicador de sincronia — o texto não vai à tela. */
  const [aviso, setAviso] = useState<string | null>(null);
  /**
   * Eventos que saíram da lista do operador mas ficaram porque têm validação
   * sem enviar. Estado separado do `aviso` de propósito: aquele acende
   * "Sem rede" no indicador, e isto aqui não é falha de rede.
   */
  const [retidos, setRetidos] = useState(0);

  const carregarLocal = useCallback(async () => {
    const [lista, numeros] = await Promise.all([listarEventos(), contagensDosEventos()]);
    setEventos(lista);
    setContagens(numeros);
  }, []);

  const atualizarDoServidor = useCallback(async () => {
    // Em demonstração não há servidor: o evento de exemplo mora no aparelho, e
    // uma chamada aqui traria lista vazia e apagaria justamente ele.
    const sessao = await carregarSessao();
    if (sessao?.demonstracao) {
      return;
    }

    setAtualizando(true);
    setAviso(null);
    try {
      const doServidor = await baixarEventos();
      await salvarEventos(doServidor);

      // A resposta chegou inteira: só agora dá para tratar ausência como
      // remoção. O evento do caixa aberto fica protegido — tirar o chão de
      // quem está validando agora seria pior que mostrar um evento a mais.
      //
      // Lido do armazenamento, não do estado da tela nem do cache em memória:
      // os dois são preenchidos por um efeito que corre em paralelo com este,
      // e perder essa corrida significaria apagar o evento em uso.
      const aberto = await caixaAberto();
      const {removidos, retidosPorPendencia} = await removerEventosForaDaLista(
        doServidor.map(e => e.id),
        aberto?.eventoId === undefined ? [] : [aberto.eventoId],
      );

      // Mesmo motivo do log de `[api]`: quando o operador diz "o evento não
      // sumiu", só isto separa "o servidor ainda manda" de "o app não apagou".
      // Ids, não nomes — é por id que se confere no painel.
      console.log(
        `[eventos] servidor=[${doServidor.map(e => e.id).join(',')}]` +
          ` caixa=${aberto?.eventoId ?? '-'}` +
          ` removidos=[${removidos.join(',')}]` +
          ` retidos=[${retidosPorPendencia.join(',')}]`,
      );

      setRetidos(retidosPorPendencia.length);
      await carregarLocal();
    } catch (e) {
      // Sem rede o app continua utilizável com o que já baixou.
      setAviso(
        e instanceof ErroApi
          ? txt('Sem atualizar a lista: {erro}', {erro: e.message})
          : txt('Sem atualizar a lista: falha de rede'),
      );
    } finally {
      setAtualizando(false);
    }
  }, [carregarLocal]);

  useEffect(() => {
    caixaAberto().then(aberto => {
      if (!aberto) {
        navigation.replace('Caixa');
        return;
      }
      setCaixa(aberto);
    });
  }, [navigation]);

  useEffect(() => {
    carregarLocal().then(atualizarDoServidor);
  }, [carregarLocal, atualizarDoServidor]);

  function encerrarCaixa() {
    Alert.alert(txt('Fechar'), txt('Encerrar "{posto}" neste aparelho?', {posto: caixa?.nome ?? ''}), [
      {text: txt('Cancelar'), style: 'cancel'},
      {
        text: txt('Fechar'),
        style: 'destructive',
        onPress: async () => {
          await fecharCaixa();
          navigation.replace('Caixa');
        },
      },
    ]);
  }

  const sincronia = atualizando
    ? {cor: t.aviso, texto: txt('Atualizando')}
    : aviso
      ? {cor: t.erro, texto: txt('Sem rede')}
      : {cor: t.destaque, texto: txt('Sincronizado')};

  return (
    <SafeAreaView style={[estilos.tela, {backgroundColor: t.fundo}]} edges={['top', 'bottom']}>
      <View style={[estilos.cabecalho, {borderBottomColor: t.superficie}]}>
        <View style={{flex: 1}}>
          <Text style={[estilos.tituloCabecalho, {color: t.texto}]}>{txt('Eventos')}</Text>
          <Text style={[estilos.legendaCabecalho, {color: t.primaria}]} numberOfLines={1}>
            {caixa?.nome ?? ''}
          </Text>
        </View>

        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 7,
            height: 34,
            paddingHorizontal: 12,
            borderRadius: 10,
            backgroundColor: t.superficie,
            borderWidth: 1,
            borderColor: t.borda,
          }}>
          <View
            style={{width: 8, height: 8, borderRadius: 4, backgroundColor: sincronia.cor}}
          />
          <Text style={{color: t.textoMedio, fontSize: 12, fontFamily: fontes.negrito}}>
            {sincronia.texto}
          </Text>
        </View>

        {/* Buscar a lista de novo sem fechar o caixa. Puxar a lista para baixo
            faz o mesmo, mas isso é gesto que ninguém descobre sozinho — e no
            computador, com o leitor USB, não existe puxar. */}
        <Pressable
          onPress={() => atualizarDoServidor()}
          disabled={atualizando}
          hitSlop={6}
          accessibilityLabel={txt('Atualizar a lista de eventos')}
          style={[estilos.botaoIcone, {borderColor: t.borda, opacity: atualizando ? 0.4 : 1}]}>
          <RefreshCw size={20} color={t.primaria} strokeWidth={2.2} />
        </Pressable>
      </View>

      <FlatList
        data={eventos}
        keyExtractor={e => String(e.id)}
        contentContainerStyle={{padding: 20, gap: 12}}
        refreshControl={
          <RefreshControl
            refreshing={atualizando}
            onRefresh={atualizarDoServidor}
            tintColor={t.primaria}
          />
        }
        ListHeaderComponent={
          <View style={{gap: 12}}>
            <Text style={{color: t.textoFraco, fontSize: 13, fontFamily: fontes.regular}}>
              {txt('Escolha o evento desta portaria')}
            </Text>

            {retidos > 0 ? (
              <View
                style={{
                  padding: 12,
                  borderRadius: 12,
                  backgroundColor: t.avisoFundo,
                  borderWidth: 1,
                  borderColor: t.avisoBorda,
                  gap: 4,
                }}>
                <Text
                  style={{color: t.avisoTinta, fontSize: 13, fontFamily: fontes.negrito}}>
                  {retidos === 1
                    ? txt('Um evento saiu da sua lista e ficou')
                    : txt('{quantos} eventos saíram da sua lista e ficaram', {quantos: retidos})}
                </Text>
                <Text
                  style={{color: t.avisoTinta, fontSize: 12, fontFamily: fontes.regular}}>
                  {retidos === 1
                    ? txt(
                        'Ele tem validação que ainda não subiu. Entre nele com rede para a fila esvaziar; depois disso ele sai sozinho.',
                      )
                    : txt(
                        'Eles têm validação que ainda não subiu. Entre em cada um com rede para a fila esvaziar; depois disso eles saem sozinhos.',
                      )}
                </Text>
              </View>
            ) : null}
          </View>
        }
        ListEmptyComponent={
          atualizando ? (
            <ActivityIndicator color={t.primaria} />
          ) : (
            <Text style={[estilos.subtitulo, {color: t.textoFraco}]}>
              {txt('Nenhum evento disponível para este operador.')}
            </Text>
          )
        }
        renderItem={({item}) => {
          const quando = quandoAcontece(item.data);
          const numeros = contagens[item.id] ?? SEM_CONTAGEM;
          const baixado = numeros.ingressos > 0 || numeros.setores > 0;

          return (
            <Pressable
              onPress={async () => {
                // Escolher o evento amarra o caixa a ele; a tela de eventos sai
                // da pilha para não haver volta sem fechar o caixa.
                await vincularEvento(item.id, item.nome);
                navigation.replace('Setores', {eventoId: item.id, eventoNome: item.nome});
              }}
              style={({pressed}) => [
                estilos.cartao,
                {
                  backgroundColor: t.superficie,
                  borderColor: quando.hoje || pressed ? t.primaria : t.borda,
                  gap: 12,
                },
              ]}>
              <View style={{flexDirection: 'row', alignItems: 'flex-start', gap: 12}}>
                <BannerDoEvento url={item.banner} />

                <View style={{flex: 1, gap: 5}}>
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 7}}>
                    {quando.hoje ? (
                      <View
                        style={{
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          borderRadius: 6,
                          backgroundColor: t.primaria,
                        }}>
                        <Text
                          style={{
                            color: t.tintaPrimaria,
                            fontSize: 11,
                            fontFamily: fontes.extra,
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                          }}>
                          {txt('Hoje')}
                        </Text>
                      </View>
                    ) : (
                      <Text
                        style={{
                          color: t.textoFraco,
                          fontSize: 12,
                          fontFamily: fontes.regular,
                          fontVariant: ['tabular-nums'],
                        }}>
                        {quando.dia}
                      </Text>
                    )}
                    {quando.hora ? (
                      <Text
                        style={{
                          color: t.textoFraco,
                          fontSize: 12,
                          fontFamily: fontes.regular,
                          fontVariant: ['tabular-nums'],
                        }}>
                        {quando.hoje ? quando.hora : `· ${quando.hora}`}
                      </Text>
                    ) : null}
                  </View>

                  <Text
                    style={{
                      color: t.texto,
                      fontSize: quando.hoje ? 20 : 18,
                      fontFamily: quando.hoje ? fontes.extra : fontes.negrito,
                      letterSpacing: -0.4,
                    }}>
                    {item.nome}
                  </Text>

                  <Text style={{color: t.textoFraco, fontSize: 14, fontFamily: fontes.regular}}>
                    {[item.local, item.cidade].filter(Boolean).join(' · ')}
                  </Text>
                </View>

                <ChevronRight
                  size={22}
                  color={quando.hoje ? t.primaria : t.textoApagado}
                  strokeWidth={2.4}
                />
              </View>

              {baixado ? (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 18,
                    paddingTop: 12,
                    borderTopWidth: 1,
                    borderTopColor: t.borda,
                  }}>
                  {[
                    {valor: comMilhar(numeros.ingressos), rotulo: txt('ingressos'), destaque: false},
                    {valor: String(numeros.setores), rotulo: txt('setores'), destaque: false},
                    {
                      valor: comMilhar(numeros.nestaMaquina),
                      rotulo: txt('neste aparelho'),
                      destaque: numeros.nestaMaquina > 0,
                    },
                  ].map(coluna => (
                    <View key={coluna.rotulo}>
                      <Text
                        style={[
                          estilos.numero,
                          {fontSize: 18, color: coluna.destaque ? t.destaque : t.texto},
                        ]}>
                        {coluna.valor}
                      </Text>
                      <Text
                        style={{
                          color: t.textoApagado,
                          fontSize: 11,
                          fontFamily: fontes.regular,
                          letterSpacing: 0.6,
                          textTransform: 'uppercase',
                        }}>
                        {coluna.rotulo}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </Pressable>
          );
        }}
      />

      <View style={{padding: 20, paddingTop: 4, gap: 10}}>
        <Pressable
          onPress={encerrarCaixa}
          style={[estilos.botaoSecundario, {borderColor: t.borda}]}>
          <Lock size={18} color={t.textoFraco} strokeWidth={2} />
          <Text style={[estilos.textoBotaoSecundario, {color: t.textoFraco}]}>{txt('Fechar')}</Text>
        </Pressable>
        <Versao />
      </View>
    </SafeAreaView>
  );
}
